import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { WalkRangementBlock } from '@/components/admin/sessions/walk/walk-rangement-block';
import { Button } from '@/components/ui/button';
import {
  RANGEMENT_TRIALS_KEY,
  RANGEMENT_TRIAL_KEY,
  fetchRangementTrial,
  fetchRangementTrials,
  startRangementTrial,
} from '@/lib/session-api';
import { costLabel, sessionMoment } from '@/lib/session-labels';
import {
  RANGEMENT_STATE_LABEL,
  RANGEMENT_TRIAL_REFUSAL_SENTENCE,
  rangementEditorSearch,
  rangementFingerprint,
  rangementTone,
  rangementTrialOutcome,
  rangementTrialRefusalOf,
} from '@/lib/session-rangement';
import { cn } from '@/lib/utils';
import type { RangementTrial, SessionDetail } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * « Rangement à blanc » - the TRIALS of the ranger on the session being read
 * (scenario-succes.md §7.3, issue #177), at the pattern of `walk-noyau-trial.tsx`.
 *
 * ## What the button does
 *
 * It plays the ranger on the CONVERSATION of this session, with the Rangement
 * section of the protocol's development version - the consigne being tuned -,
 * and draws the result in the column of the ranger, marked « à blanc, vN ». The
 * trial is kept with the session once settled, and nothing else is written:
 * neither the session's rangement, if it has one, nor its determination, nor its
 * cost. So the section is on every session that played a protocol, those whose
 * protocol had no target at the time included: that is its use.
 *
 * ## Which trial the block draws
 *
 * The newest the session keeps, unless the operator picked another in the list
 * under it. A press draws the trial it started. The list is the way back to an
 * older consigne: two columns of two versions, one session.
 *
 * ## Why the screen reads a trial again
 *
 * The ranger may think for minutes and retry once, and the proxy cuts a request
 * at sixty seconds: the start answers at once with the trial PENDING, seeded into
 * its own cache key, and the trial is read again every `TRIAL_POLL_MS` until it
 * settles - then never again, a settled trial being unable to change, and the
 * list is read again to learn it. A read that FAILS stops the reading too, and
 * frees the button: the data it keeps says pending, of a trial a restart of the
 * server lost on its way. A second press during a trial returns that trial
 * rather than paying the ranger twice, and the button says so by being closed.
 *
 * ## What a refusal says
 *
 * The four refusals are worded from their `code` (`RANGEMENT_TRIAL_REFUSAL_SENTENCE`),
 * and a development version with no target gets the way to write one: the
 * protocol's editor, on its Rangement view. A refused press draws no trial under
 * its sentence - the column would read as the refused attempt's - until one is
 * picked in the list.
 */

/** How often a running trial is read again: the ranger answers in one to three minutes. */
const TRIAL_POLL_MS = 1500;

export interface WalkRangementTrialProps {
  /** The session being read: the block draws its scribe's column and names its steps. */
  readonly detail: SessionDetail;
  /** The protocol the session played - where a missing Rangement gets written. */
  readonly protocolId: string;
}

export function WalkRangementTrial({ detail, protocolId }: WalkRangementTrialProps) {
  const sessionId = detail.session.id;
  const client = useQueryClient();
  // The trial picked in the list, or started here; null draws the newest kept.
  const [picked, setPicked] = useState<string | null>(null);

  const list = useQuery({
    queryKey: RANGEMENT_TRIALS_KEY(sessionId),
    queryFn: () => fetchRangementTrials(sessionId),
    refetchOnWindowFocus: false,
  });
  const trials = list.data?.trials ?? [];

  const start = useMutation({
    mutationFn: () => startRangementTrial(sessionId),
    onSuccess: (trial) => {
      // Seeded, so the block draws the pending trial at once: the reads that
      // follow only move it forward.
      client.setQueryData(RANGEMENT_TRIAL_KEY(sessionId, trial.id), trial);
      setPicked(trial.id);
      void client.invalidateQueries({ queryKey: RANGEMENT_TRIALS_KEY(sessionId) });
    },
  });

  const shownId = start.isError ? null : (picked ?? trials[0]?.id ?? null);
  const shownKey = RANGEMENT_TRIAL_KEY(sessionId, shownId ?? '');

  const shown = useQuery({
    queryKey: shownKey,
    queryFn: async () => {
      const before = client.getQueryData<RangementTrial>(shownKey);
      const trial = await fetchRangementTrial(sessionId, shownId ?? '');
      // Settled since the last read: the list still says it runs.
      if (before?.state === 'pending' && trial.state !== 'pending') {
        void client.invalidateQueries({ queryKey: RANGEMENT_TRIALS_KEY(sessionId) });
      }
      return trial;
    },
    enabled: shownId !== null,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    // A failed read keeps the data before it, which may say PENDING: the error
    // stops the reading, or a trial the server lost would be asked for every
    // 1.5 s for as long as the tab stays open.
    refetchInterval: (query) =>
      query.state.status !== 'error' && query.state.data?.state === 'pending'
        ? TRIAL_POLL_MS
        : false,
  });

  const running = start.isPending || (!shown.isError && shown.data?.state === 'pending');
  const refusal = rangementTrialRefusalOf(start.error);

  return (
    <section data-testid={SESSIONS_TEST_IDS.rangementTrial} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-300 border-dashed bg-white px-4 py-3">
        <div className="flex max-w-[80ch] flex-col gap-1">
          <p className="text-caption text-gray-600">
            Rejoue le rangeur sur la conversation de cette session, avec la consigne de la version
            de développement du protocole. Chaque essai est gardé avec la session : ni son
            rangement, ni sa détermination, ni son coût ne changent.
          </p>
          {running ? (
            <p className="text-gray-500 text-xs">
              Un second clic pendant l’essai rend celui-ci, plutôt que de payer le rangeur deux
              fois. On peut quitter la page : l’essai se relit au retour.
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={SESSIONS_TEST_IDS.rangementTrialRun}
          disabled={running}
          onClick={() => start.mutate()}
        >
          {start.isPending
            ? 'Lancement…'
            : running
              ? 'Le rangeur relit…'
              : trials.length === 0 && picked === null
                ? 'Lancer le rangeur'
                : 'Relancer le rangeur'}
        </Button>
      </div>

      {refusal !== null ? (
        <p
          role="alert"
          data-testid={SESSIONS_TEST_IDS.rangementTrialRefusal}
          data-code={refusal}
          className="flex flex-wrap gap-x-3 gap-y-1 text-caption text-gray-700"
        >
          <span>{RANGEMENT_TRIAL_REFUSAL_SENTENCE[refusal]}</span>
          {refusal === 'rangement-missing' ? (
            <Link
              to="/admin/protocoles/$protocolId"
              params={{ protocolId }}
              search={rangementEditorSearch(null)}
              className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Ouvrir le rangement du protocole
            </Link>
          ) : null}
        </p>
      ) : start.isError ? (
        <p role="alert" className="text-caption text-rouge">
          Le rangeur n’a pas pu être lancé. Réessayez.
        </p>
      ) : null}

      {list.isError ? (
        <p role="alert" className="text-caption text-rouge">
          Les essais gardés avec cette session n’ont pas pu être lus.
        </p>
      ) : null}

      {shown.isError ? (
        <p role="alert" className="text-caption text-gray-700">
          Cet essai n’a pas pu aller au bout, sans doute un redémarrage du serveur pendant qu’il
          tournait : relancez le rangeur.
        </p>
      ) : null}

      {/* Not under the sentence above: the data kept by a failed read says pending. */}
      {shown.data === undefined || shown.isError ? null : (
        <WalkRangementBlock detail={detail} trial={shown.data} />
      )}

      {trials.length === 0 ? null : (
        <nav
          aria-label="Essais de cette session"
          data-testid={SESSIONS_TEST_IDS.rangementTrialList}
        >
          <table className="w-full border-collapse text-caption leading-snug">
            <caption className="pb-1.5 text-left font-semibold text-gray-500 text-xs uppercase tracking-wide">
              Essais de cette session
            </caption>
            <thead>
              <tr className="border-gray-200 border-b text-left text-gray-600">
                <th scope="col" className="py-1.5 pr-3 font-semibold">
                  Moment
                </th>
                <th scope="col" className="py-1.5 pr-3 font-semibold">
                  Consigne
                </th>
                <th scope="col" className="py-1.5 pr-3 font-semibold">
                  Rendu
                </th>
                <th scope="col" className="py-1.5 text-right font-semibold">
                  Coût
                </th>
              </tr>
            </thead>
            <tbody>
              {trials.map((trial) => {
                const current = trial.id === shownId;
                const fingerprint = rangementFingerprint(trial);
                return (
                  <tr
                    key={trial.id}
                    className={cn('border-gray-100 border-b', current && 'bg-bleu-nuit-tint')}
                  >
                    <td className="py-2 pr-3 pl-1.5">
                      <button
                        type="button"
                        data-testid={SESSIONS_TEST_IDS.rangementTrialListItem}
                        data-trial={trial.id}
                        data-state={trial.state}
                        aria-current={current ? 'true' : undefined}
                        onClick={() => {
                          start.reset();
                          setPicked(trial.id);
                        }}
                        className={cn(
                          'cursor-pointer text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          current ? 'font-bold' : 'font-semibold underline underline-offset-3',
                        )}
                      >
                        {sessionMoment(trial.startedAt)}
                      </button>
                    </td>
                    <td className="py-2 pr-3">
                      v{trial.protocolVersion.version}
                      {fingerprint === null ? null : (
                        <>
                          {' · '}
                          <code className="font-mono text-xs">{fingerprint}</code>
                        </>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-800">
                      {trial.state === 'rendered' ? (
                        rangementTrialOutcome(trial)
                      ) : (
                        <span
                          className={cn(
                            'rounded-full px-2 py-px font-semibold text-xs',
                            rangementTone(trial.state) === 'danger'
                              ? 'bg-rouge-tint text-rouge-deep'
                              : 'bg-bleu-nuit-tint text-bleu-nuit',
                          )}
                        >
                          {RANGEMENT_STATE_LABEL[trial.state].toLowerCase()}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">{costLabel(trial.costEur)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </nav>
      )}
    </section>
  );
}
